const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/portal/service-contract`
const TOKEN_KEY = 'jangi_sc_token'

export type ServiceContractPdfTab =
  | 'all'
  | 'contract'
  | 'annex1'
  | 'annex2'
  | 'annex3'
  | 'annex4'
  | 'annex5'
  | 'summary'

/** Node(PDFKit)로 현재 탭 계약서 PDF를 받아 Blob 으로 돌려준다. */
export async function fetchServiceContractPdf(payload: {
  tab: ServiceContractPdfTab
  form: Record<string, unknown>
  disposition?: 'inline' | 'attachment'
}): Promise<Blob> {
  const response = await fetch(`${BASE}/pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tab: payload.tab,
      form: payload.form,
      disposition: payload.disposition || 'inline',
    }),
  })

  const contentType = response.headers.get('content-type') || ''
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const data = (await response.json()) as { message?: string }
      throw new Error(data.message || 'PDF를 만들지 못했습니다.')
    }
    throw new Error('PDF 서버에 연결할 수 없습니다.')
  }
  if (!contentType.includes('application/pdf')) {
    throw new Error('PDF 응답이 올바르지 않습니다.')
  }
  return response.blob()
}

export type ServiceContractListItem = {
  id: number
  orgName: string
  facilityCode: string
  legalName: string
  representative: string
  wageSelect: string
  acctSelect: string
  annex2Mode: 'form' | 'text'
  hasSign: boolean
  createdAt: string
  updatedAt: string
}

export function getContractToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setContractToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token)
}

export function clearContractToken() {
  try {
    sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

class ContractApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function contractFetch(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  const token = getContractToken()
  if (token) headers.set('x-contract-token', token)
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })
  const contentType = response.headers.get('content-type') || ''
  const data = contentType.includes('application/json')
    ? ((await response.json()) as { success?: boolean; message?: string })
    : null
  if (response.status === 401) {
    if (path !== '/unlock') clearContractToken()
    throw new ContractApiError(data?.message || '비밀번호가 맞지 않습니다.', 401)
  }
  if (!response.ok || data?.success === false) {
    throw new ContractApiError(data?.message || '계약서를 처리하지 못했습니다.', response.status)
  }
  return data || {}
}

export async function unlockServiceContract(password: string) {
  const data = (await contractFetch('/unlock', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })) as { token?: string }
  if (!data.token) throw new Error('비밀번호 확인에 실패했습니다.')
  setContractToken(data.token)
  return data.token
}

export async function fetchServiceContractList() {
  const data = (await contractFetch('')) as { items?: ServiceContractListItem[] }
  return data.items || []
}

export async function fetchServiceContract(id: number) {
  const data = (await contractFetch(`/${id}`)) as { id?: number; form?: Record<string, unknown> }
  return { id: data.id || id, form: data.form || {} }
}

export async function saveServiceContract(id: number | null, form: Record<string, unknown>) {
  if (id) {
    const data = (await contractFetch(`/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ form }),
    })) as { id?: number }
    return data.id || id
  }
  const data = (await contractFetch('', {
    method: 'POST',
    body: JSON.stringify({ form }),
  })) as { id?: number }
  if (!data.id) throw new Error('저장 결과를 확인하지 못했습니다.')
  return data.id
}

export async function deleteServiceContract(id: number, password: string) {
  await contractFetch(`/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  })
}

export function isContractAuthError(error: unknown) {
  return error instanceof ContractApiError && error.status === 401
}
